import React, { useState, useEffect } from 'react';
import { 
  Container, Typography, Paper, FormControl, InputLabel, Select, MenuItem, Button, 
  TextField, Switch, FormControlLabel, Box, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Link
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { getMode, updateMode, getAllSettings, addOrUpdateSetting, getSetting, deleteAllSheets } from '../services/api';
import { extractXlsxData, getItemCells, validateXlsxFile, formatFileSize, debugXlsxFile } from '../services/xlsxExtractorApi';
import { useNavigate } from 'react-router-dom';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import InfoIcon from '@mui/icons-material/Info';

const StyledContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

const Title = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const StyledButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

const RecoverButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

const StyledLink = styled(Link)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'underline',
  },
}));

function Settings() {
  const [mode, setMode] = useState('');
  const [newSetting, setNewSetting] = useState({ key: '', value: '', type: 'string' });
  const [allSettings, setAllSettings] = useState([]);
  const [message, setMessage] = useState('');
  const navigate = useNavigate();
  const [useOldSales, setUseOldSales] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingUseOldSales, setPendingUseOldSales] = useState(false);
  const [disableCacheLocations, setDisableCacheLocations] = useState(false);
  const [pendingDisableCacheLocations, setPendingDisableCacheLocations] = useState(false);
  const [deleteSheetDialogOpen, setDeleteSheetDialogOpen] = useState(false);
  const [deleteSheetStatus, setDeleteSheetStatus] = useState('');
  const [openInNewTab, setOpenInNewTab] = useState(() => {
    return localStorage.getItem('openInNewTab') === 'true';
  });
  
  // XLSX Extractor state
  const [xlsxFile, setXlsxFile] = useState(null);
  const [xlsxLoading, setXlsxLoading] = useState(false);
  const [xlsxResult, setXlsxResult] = useState(null);
  const [xlsxError, setXlsxError] = useState(null);
  const [itemCells, setItemCells] = useState(null);
  const [showItemCells, setShowItemCells] = useState(false);
  const [debugResult, setDebugResult] = useState(null);

  useEffect(() => {
    fetchMode();
    fetchAllSettings();
    fetchUseOldSales();
    fetchDisableCacheLocations();
  }, []);

  const fetchMode = async () => {
    try {
      const response = await getMode();
      setMode(response.data.mode);
    } catch (error) {
      console.error('Error fetching mode:', error);
    }
  };

  const fetchAllSettings = async () => {
    try {
      const response = await getAllSettings();
      setAllSettings(response.data);
    } catch (error) {
      console.error('Error fetching all settings:', error);
    }
  };

  const fetchUseOldSales = async () => {
    try {
      const response = await getSetting('UseOldSales');
      setUseOldSales(response.data.value === 'true');
    } catch (error) {
      console.error('Error fetching UseOldSales setting:', error);
    }
  };

  const fetchDisableCacheLocations = async () => {
    try {
      const response = await getSetting('Disable_Cache_Locations');
      setDisableCacheLocations(response.data.value === 'true');
    } catch (error) {
      console.error('Error fetching Disable_Cache_Locations setting:', error);
    }
  };

  const handleModeChange = async (event) => {
    const newMode = event.target.value;
    try {
      await updateMode(newMode);
      setMode(newMode);
      setMessage('Mode updated successfully');
    } catch (error) {
      console.error('Error updating mode:', error);
      setMessage('Failed to update mode');
    }
  };

  const handleNewSettingChange = (event) => {
    const { name, value } = event.target;
    setNewSetting(prev => ({ ...prev, [name]: value }));
  };

  const handleAddSetting = async (event) => {
    event.preventDefault();
    try {
      await addOrUpdateSetting(newSetting);
      setMessage('Setting added successfully');
      setNewSetting({ key: '', value: '', type: 'string' });
      fetchAllSettings();
    } catch (error) {
      console.error('Error adding setting:', error);
      setMessage('Failed to add setting');
    }
  };

  const handleRecoverLocationsClick = () => {
    navigate('/deleted-locations');
  };

  const handleUseOldSalesChange = (event) => {
    setPendingUseOldSales(event.target.checked);
    setConfirmDialogOpen(true);
  };

  const handleDisableCacheLocationsChange = (event) => {
    setPendingDisableCacheLocations(event.target.checked);
    setConfirmDialogOpen(true);
  };

  const handleConfirmChange = async () => {
    try {
      await addOrUpdateSetting({ key: 'Disable_Cache_Locations', value: pendingDisableCacheLocations.toString(), type: 'boolean' });
      setDisableCacheLocations(pendingDisableCacheLocations);
      setMessage('Disable_Cache_Locations setting updated successfully');
    } catch (error) {
      console.error('Error updating Disable_Cache_Locations setting:', error);
      setMessage('Failed to update Disable_Cache_Locations setting');
    } finally {
      setConfirmDialogOpen(false);
    }
  };

  const handleCancelChange = () => {
    setConfirmDialogOpen(false);
  };

  const handleDeleteAllSheets = () => {
    setDeleteSheetDialogOpen(true);
  };

  const handleConfirmDeleteSheets = async () => {
    try {
      setDeleteSheetStatus('Deleting sheets...');
      const result = await deleteAllSheets();
      
      if (result.success) {
        const successMessage = `Successfully deleted ${result.deleted.length} out of ${result.totalFound} sheets`;
        
        const failureMessage = result.failed.length > 0 
          ? `\n${result.failed.length} sheets failed to delete.` 
          : '';
          
        setDeleteSheetStatus(successMessage + failureMessage);
        
        console.log('Delete operation details:', {
          successful: result.deleted,
          failed: result.failed
        });
      } else {
        setDeleteSheetStatus('Failed to delete sheets: ' + result.message);
      }
    } catch (error) {
      setDeleteSheetStatus('Error deleting sheets: ' + (error.message || 'Unknown error'));
      console.error('Sheet deletion error:', error);
    } finally {
      setDeleteSheetDialogOpen(false);
      setTimeout(() => setDeleteSheetStatus(''), 5000);
    }
  };

  const handleOpenInNewTabChange = (event) => {
    const newValue = event.target.checked;
    setOpenInNewTab(newValue);
    localStorage.setItem('openInNewTab', newValue);
    setMessage('Navigation setting updated successfully');
  };

  // XLSX Extractor handlers
  const handleXlsxFileChange = (event) => {
    const file = event.target.files[0];
    setXlsxFile(file);
    setXlsxError(null);
    setXlsxResult(null);
    
    if (file && !validateXlsxFile(file)) {
      setXlsxError('Please select a valid XLSX or XLS file');
      setXlsxFile(null);
    }
  };

  const handleXlsxUpload = async () => {
    if (!xlsxFile) {
      setXlsxError('Please select a file first');
      return;
    }

    setXlsxLoading(true);
    setXlsxError(null);
    setXlsxResult(null);

    try {
      const result = await extractXlsxData(xlsxFile);
      setXlsxResult(result);
      setMessage('XLSX data extracted successfully!');
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to extract XLSX data';
      setXlsxError(errorMessage);
      setMessage('Failed to extract XLSX data');
    } finally {
      setXlsxLoading(false);
    }
  };

  const handleGetItemCells = async () => {
    try {
      const result = await getItemCells();
      setItemCells(result);
      setShowItemCells(true);
      setMessage('Item cells fetched successfully');
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch item cells';
      setMessage('Failed to fetch item cells: ' + errorMessage);
    }
  };

  const handleDebugXlsx = async () => {
    if (!xlsxFile) {
      setXlsxError('Please select a file first');
      return;
    }

    setXlsxLoading(true);
    setXlsxError(null);

    try {
      const result = await debugXlsxFile(xlsxFile);
      setDebugResult(result);
      setMessage('XLSX file structure analyzed successfully!');
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to debug XLSX file';
      setXlsxError(errorMessage);
      setMessage('Failed to debug XLSX file');
    } finally {
      setXlsxLoading(false);
    }
  };

  const handleClearXlsxResults = () => {
    setXlsxFile(null);
    setXlsxResult(null);
    setXlsxError(null);
    setItemCells(null);
    setShowItemCells(false);
    setDebugResult(null);
    // Reset file input
    const fileInput = document.getElementById('xlsx-file-input');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  return (
    <StyledContainer>
      <Title variant="h4" gutterBottom>
        Settings
      </Title>
      
      <StyledPaper>
        <Title variant="h6" gutterBottom>
          Application Mode
        </Title>
        <FormControl fullWidth>
          <InputLabel id="mode-select-label">Mode</InputLabel>
          <Select
            labelId="mode-select-label"
            value={mode}
            onChange={handleModeChange}
            label="Mode"
          >
            <MenuItem value="production">Production</MenuItem>
            <MenuItem value="development">Development</MenuItem>
          </Select>
        </FormControl>
      </StyledPaper>

      <StyledPaper>
        <Title variant="h6" gutterBottom>
          Use Old Sales
        </Title>
        <FormControlLabel
          control={
            <Switch
              checked={useOldSales}
              onChange={handleUseOldSalesChange}
              name="useOldSales"
              color="primary"
            />
          }
          label={useOldSales ? 'Enabled' : 'Disabled'}
        />
      </StyledPaper>

      <StyledPaper>
        <Title variant="h6" gutterBottom>
          Disable Cache Locations
        </Title>
        <FormControlLabel
          control={
            <Switch
              checked={disableCacheLocations}
              onChange={handleDisableCacheLocationsChange}
              name="disableCacheLocations"
              color="primary"
            />
          }
          label={disableCacheLocations ? 'Enabled' : 'Disabled'}
        />
      </StyledPaper>

      <StyledPaper>
        <Title variant="h6" gutterBottom>
          Navigation Settings
        </Title>
        <FormControlLabel
          control={
            <Switch
              checked={openInNewTab}
              onChange={handleOpenInNewTabChange}
              name="openInNewTab"
              color="primary"
            />
          }
          label={`Open links in ${openInNewTab ? 'new tab' : 'same tab'}`}
        />
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          When enabled, clicking on navigation items will open them in a new tab
        </Typography>
      </StyledPaper>

      <StyledPaper>
        <Title variant="h6" gutterBottom>
          Add New Setting
        </Title>
        <form onSubmit={handleAddSetting}>
          <TextField
            fullWidth
            label="Key"
            name="key"
            value={newSetting.key}
            onChange={handleNewSettingChange}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Value"
            name="value"
            value={newSetting.value}
            onChange={handleNewSettingChange}
            margin="normal"
            required
          />
          <FormControl fullWidth margin="normal">
            <InputLabel id="type-select-label">Type</InputLabel>
            <Select
              labelId="type-select-label"
              name="type"
              value={newSetting.type}
              onChange={handleNewSettingChange}
              label="Type"
            >
              <MenuItem value="string">String</MenuItem>
              <MenuItem value="number">Number</MenuItem>
              <MenuItem value="boolean">Boolean</MenuItem>
            </Select>
          </FormControl>
          <StyledButton type="submit" variant="contained" fullWidth>
            Add Setting
          </StyledButton>
        </form>
      </StyledPaper>

      <StyledPaper>
        <Title variant="h6" gutterBottom>
          All Settings
        </Title>
        {allSettings.map((setting) => (
          <Box key={setting.key} mb={2}>
            <Typography variant="subtitle1">{setting.key}</Typography>
            {setting.type === 'boolean' ? (
              <FormControlLabel
                control={
                  <Switch
                    checked={setting.value === 'true'}
                    onChange={() => {/* Implement update logic */}}
                    name={setting.key}
                    color="primary"
                  />
                }
                label={setting.value}
              />
            ) : (
              <Typography variant="body2">{setting.value}</Typography>
            )}
          </Box>
        ))}
      </StyledPaper>

      <StyledPaper>
        <Title variant="h6" gutterBottom>
          Recover Deleted Locations
        </Title>
        <RecoverButton variant="contained" color="primary" onClick={handleRecoverLocationsClick}>
          View Deleted Locations
        </RecoverButton>
      </StyledPaper>

      <StyledPaper>
        <Title variant="h6" gutterBottom>
          System Status
        </Title>
        <StyledLink 
          href="https://stats.uptimerobot.com/OQ1epxzNfS"
          target="_blank"
          rel="noopener noreferrer"
          color="primary"
        >
          View System Uptime Dashboard
          <OpenInNewIcon fontSize="small" />
        </StyledLink>
      </StyledPaper>

      <StyledPaper>
        <Title variant="h6" gutterBottom>
          XLSX Data Extractor
        </Title>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Upload an XLSX file to extract item data using the same logic as Google Sheets. 
          The file should have sheets named according to item types (G, M, G2, M2, G1, M1, etc.).
        </Typography>
        
        <Box display="flex" flexDirection="column" gap={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <input
              id="xlsx-file-input"
              type="file"
              accept=".xlsx,.xls"
              onChange={handleXlsxFileChange}
              style={{ display: 'none' }}
            />
            <label htmlFor="xlsx-file-input">
              <Button
                variant="outlined"
                component="span"
                startIcon={<UploadFileIcon />}
                disabled={xlsxLoading}
              >
                Select XLSX File
              </Button>
            </label>
            
            {xlsxFile && (
              <Typography variant="body2" color="textSecondary">
                {xlsxFile.name} ({formatFileSize(xlsxFile.size)})
              </Typography>
            )}
          </Box>

          <Box display="flex" gap={2}>
            <Button
              variant="contained"
              onClick={handleXlsxUpload}
              disabled={!xlsxFile || xlsxLoading}
              startIcon={<UploadFileIcon />}
            >
              {xlsxLoading ? 'Extracting...' : 'Extract Data'}
            </Button>
            
            <Button
              variant="outlined"
              onClick={handleGetItemCells}
              startIcon={<InfoIcon />}
            >
              View Item Cells
            </Button>
            
            <Button
              variant="outlined"
              onClick={handleDebugXlsx}
              disabled={!xlsxFile || xlsxLoading}
              color="info"
            >
              Debug File Structure
            </Button>
            
            {(xlsxResult || xlsxError || itemCells || debugResult) && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleClearXlsxResults}
              >
                Clear Results
              </Button>
            )}
          </Box>

          {xlsxError && (
            <Typography color="error" sx={{ mt: 1 }}>
              Error: {xlsxError}
            </Typography>
          )}

          {xlsxResult && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom>
                Extraction Results
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Total Items:</strong> {xlsxResult.data?.summary?.totalItems || 0}
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Item Types:</strong> {xlsxResult.data?.summary?.itemTypes || 0}
              </Typography>
              
              {xlsxResult.data?.summary?.grandTotalValue && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Grand Total Value:</strong> {xlsxResult.data.summary.grandTotalValue.toLocaleString()}
                </Typography>
              )}
              
              {xlsxResult.data?.summary?.typeBreakdown && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Type Breakdown:</strong>
                  </Typography>
                  {Object.entries(xlsxResult.data.summary.typeBreakdown).map(([type, breakdown]) => (
                    <Box key={type} sx={{ ml: 2, mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {type}: {typeof breakdown === 'object' ? breakdown.itemCount : breakdown} items
                      </Typography>
                      {typeof breakdown === 'object' && (
                        <Box sx={{ ml: 2 }}>
                          <Typography variant="body2" color="textSecondary">
                            Total Value: {breakdown.totalValue.toLocaleString()}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Average Value: {breakdown.averageValue.toLocaleString()}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
              
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Raw Response:</strong>
              </Typography>
              <Box sx={{ 
                maxHeight: 300, 
                overflow: 'auto', 
                bgcolor: 'grey.100', 
                p: 1, 
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '0.8rem'
              }}>
                <pre>{JSON.stringify(xlsxResult, null, 2)}</pre>
              </Box>
            </Box>
          )}

          {debugResult && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom>
                File Structure Debug
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Filename:</strong> {debugResult.data?.filename}
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>File Size:</strong> {formatFileSize(debugResult.data?.fileSize || 0)}
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Total Sheets:</strong> {debugResult.data?.totalSheets || 0}
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Sheet Names:</strong> {debugResult.data?.sheetNames?.join(', ') || 'None'}
              </Typography>
              
              {debugResult.data?.sheetDetails && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Sheet Details:</strong>
                  </Typography>
                  {Object.entries(debugResult.data.sheetDetails).map(([sheetName, details]) => (
                    <Box key={sheetName} sx={{ ml: 2, mb: 1 }}>
                      <Typography variant="body2">
                        <strong>{sheetName}:</strong> {details.rowCount} rows × {details.colCount} cols
                      </Typography>
                      {details.sampleData && details.sampleData.length > 0 && (
                        <Typography variant="body2" sx={{ ml: 2, fontSize: '0.75rem', color: 'text.secondary' }}>
                          Sample: {JSON.stringify(details.sampleData[0])}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
              
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Raw Response:</strong>
              </Typography>
              <Box sx={{ 
                maxHeight: 300, 
                overflow: 'auto', 
                bgcolor: 'grey.100', 
                p: 1, 
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '0.8rem'
              }}>
                <pre>{JSON.stringify(debugResult, null, 2)}</pre>
              </Box>
            </Box>
          )}

          {showItemCells && itemCells && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom>
                Item Cells Information
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Total Cells:</strong> {itemCells.data?.totalCells || 0}
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Type Count:</strong> {itemCells.data?.typeCount || 0}
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Raw Response:</strong>
              </Typography>
              <Box sx={{ 
                maxHeight: 300, 
                overflow: 'auto', 
                bgcolor: 'grey.100', 
                p: 1, 
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '0.8rem'
              }}>
                <pre>{JSON.stringify(itemCells, null, 2)}</pre>
              </Box>
            </Box>
          )}
        </Box>
      </StyledPaper>

      <StyledPaper>
        <Title variant="h6" gutterBottom>
          Google Sheets Management
        </Title>
        <Box display="flex" flexDirection="column" gap={2}>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteForeverIcon />}
            onClick={handleDeleteAllSheets}
          >
            Delete All Google Sheets
          </Button>
          {deleteSheetStatus && (
            <Typography color="info" mt={1}>
              {deleteSheetStatus}
            </Typography>
          )}
        </Box>
      </StyledPaper>

      {message && (
        <Typography color="primary" align="center" mt={2}>
          {message}
        </Typography>
      )}

      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelChange}
      >
        <DialogTitle>Confirm Change</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to change the {pendingDisableCacheLocations ? 'Disable Cache Locations' : 'Use Old Sales'} setting to {pendingDisableCacheLocations ? 'Enabled' : 'Disabled'}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelChange} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmChange} color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteSheetDialogOpen}
        onClose={() => setDeleteSheetDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete All Sheets</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete all Google Sheets? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteSheetDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDeleteSheets} color="error">
            Delete All
          </Button>
        </DialogActions>
      </Dialog>
    </StyledContainer>
  );
}

export default Settings;